import { useCallback, useMemo } from 'react';

import { useActorRef, useSelector } from '@xstate/react';
import { type ActorRefFrom, type InputFrom } from 'xstate';

import { scrollspyVirtualizedMachine } from './machine';
import { type ScrollspyVirtualizedAction } from './machine/scrollspy-virtualized.types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Scrollspy for virtualized lists such as FlashList, where offscreen rows are
 * unmounted and targets cannot be measured.
 *
 * Maps target ids to row indices so a jump can go through `scrollToIndex`, and
 * feeds the list's viewability reports to the machine resolving the active
 * target.
 *
 * @example
 * const listRef = useRef<FlashListRef<Row>>(null);
 *
 * const {
 *   actorRef,
 *   initialScrollIndex,
 *   onViewableItemsChanged,
 *   scrollTo,
 * } = useVirtualizedScrollspy({ rows, getTargetId, listRef, defaultTargetId });
 *
 * // inside a memoized nav component
 * const activeTargetId = useScrollspyActiveTargetId(actorRef);
 *
 * <NavItem isActive={activeTargetId === targetId} onPress={scrollTo} />;
 *
 * <FlashList
 *   ref={listRef}
 *   data={rows}
 *   initialScrollIndex={initialScrollIndex}
 *   onViewableItemsChanged={onViewableItemsChanged}
 * />;
 */
export const useVirtualizedScrollspy = <Row>(params: Params<Row>) => {
  const { rows, getTargetId, listRef, defaultTargetId, onActiveTargetIdChange } = params;

  //
  // ─── Machine ─────────────────────────────────────────────────────────

  const handleActiveTargetChange = useCallback<ScrollspyVirtualizedAction>(
    ({ context }) => {
      if (context.activeTargetId === undefined) return;

      onActiveTargetIdChange?.(context.activeTargetId);
    },
    [onActiveTargetIdChange],
  );

  const actorRef = useActorRef(
    scrollspyVirtualizedMachine.provide({
      actions: { onActiveTargetChange: handleActiveTargetChange },
    }),
    { input: { defaultTargetId, listRef } },
  );

  const initialTargetId = useSelector(actorRef, (snapshot) => snapshot.context.initialTargetId);

  // ─── Derived ─────────────────────────────────────────────────────────

  const findRowIndex = useCallback(
    (targetId: string) => rows.findIndex((row) => getTargetId(row) === targetId),
    [rows, getTargetId],
  );

  const initialScrollIndex = useMemo(() => {
    if (initialTargetId === undefined) return undefined;

    const index = findRowIndex(initialTargetId);

    return index === -1 ? undefined : index;
  }, [findRowIndex, initialTargetId]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const scrollTo = useCallback(
    (targetId: string) => {
      const index = findRowIndex(targetId);

      if (index === -1) return;

      actorRef.send({ type: 'SCROLL_TO_TARGET', targetId, index });
    },
    [actorRef, findRowIndex],
  );

  const onViewableItemsChanged = useCallback(
    (info: ViewableItemsChangedInfo<Row>) => {
      const targetIds = info.viewableItems.map((token) => getTargetId(token.item));

      actorRef.send({ type: 'SET_VISIBLE_TARGETS', targetIds });
    },
    [actorRef, getTargetId],
  );

  // ─────────────────────────────────────────────────────────────────────

  return { actorRef, initialScrollIndex, scrollTo, onViewableItemsChanged };
};

/**
 * Subscribed separately so only the nav re-renders as sections are crossed.
 */
export const useScrollspyActiveTargetId = (actorRef: ScrollspyActorRef) => {
  return useSelector(actorRef, (snapshot) => snapshot.context.activeTargetId);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params<Row> = {
  rows: readonly Row[];
  getTargetId: (row: Row) => string;
  listRef: InputFrom<typeof scrollspyVirtualizedMachine>['listRef'];
  defaultTargetId?: string;
  onActiveTargetIdChange?: (targetId: string) => void;
};

type ViewableItemsChangedInfo<Row> = {
  viewableItems: ReadonlyArray<{ item: Row }>;
};

type ScrollspyActorRef = ActorRefFrom<typeof scrollspyVirtualizedMachine>;
